import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { getOrganizationContext, getOrganizationHeaders } from '../orgContext';
import CourseAnalytics from './CourseAnalytics';
import PerformanceChart from './PerformanceChart';
import StudyStatistics from './StudyStatistics';

interface UserProgress {
    totalCoursesEnrolled: number;
    coursesCompleted: number;
    averageCourseProgress: number;
    totalPracticeAttempts: number;
    averagePracticeScore: number;
    bestPracticeScore: number;
    totalStudyMinutes: number;
    currentStudyStreak: number;
    longestStudyStreak: number;
    questionsAnswered: number;
    questionsCorrect: number;
    overallAccuracy: number;
}

interface CourseAnalyticsItem {
    courseId: number;
    courseName: string;
    completionPercentage: number;
    isCompleted: boolean;
    completionDate: string | null;
    totalMinutesSpent: number;
    practiceTestsAttempted: number;
    averageTestScore: number;
    bestTestScore: number;
    strongAreas: number;
    weakAreas: number;
    enrolledDate: string;
    lastAccessedDate: string;
}

interface EnrolledCourseSummary {
    courseId: number;
    courseName: string;
    enrollmentDate: string;
    subscriptionTier: number;
    completionPercentage: number;
    associatedExamCount: number;
    totalExamMinutes: number;
    averageExamMinutes: number;
    lastPracticeAttemptDate: string | null;
}

interface RecentTestPerformance {
    testId: number;
    testName: string;
    courseName: string;
    score: number;
    isPassed: boolean;
    attemptDate: string;
    scoreChange: number;
}

interface StudyTrend {
    dailyActivity: DailyStudy[];
    averageDailyMinutes: number;
    totalDaysActive: number;
    weeklyTrendPercentage: number;
}

interface AssignmentSummary {
    id: number;
    title: string;
    description?: string | null;
    type?: string | null;
    examId?: number | null;
    examName?: string | null;
    status?: string | null;
    dueAt?: string | null;
    questionCount?: number;
    targetCount?: number;
}

interface DailyStudy {
    date: string;
    minutesSpent: number;
    questionsAnswered: number;
    testsCompleted: number;
    averageScore: number;
}

interface DashboardData {
    userProgress: UserProgress;
    enrolledCourses: EnrolledCourseSummary[];
    courseAnalytics: CourseAnalyticsItem[];
    recentTestPerformance: RecentTestPerformance[];
    studyTrend: StudyTrend;
    assignments: AssignmentSummary[];
}

interface UpcomingSession {
    id: number;
    title?: string | null;
    startDateTime: string;
    endDateTime: string;
    teacherDisplayName?: string | null;
    classroomName?: string | null;
    status: string;
    location?: string | null;
    meetingUrl?: string | null;
}

export default function Dashboard() {
    const { isAuthenticated, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'courses' | 'performance' | 'statistics'>('overview');
    const [expandedCourseId, setExpandedCourseId] = useState<number | null>(null);
    const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && user) {
            fetchDashboardData();
            void fetchUpcomingSessions();
        }
    }, [isAuthenticated, user]);

    const fetchUpcomingSessions = async () => {
        try {
            setSessionsLoading(true);
            const token = await getAccessTokenSilently();
            const response = await fetch(`${config.apiBaseUrl}/api/schedules/my?upcomingOnly=true&includeInProgress=true`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...getOrganizationHeaders(),
                },
            });
            if (!response.ok) {
                return;
            }
            const data = await response.json();
            const rows: UpcomingSession[] = Array.isArray(data)
                ? data
                    .map((item: any) => ({
                    id: item.id,
                    title: item.title ?? null,
                    startDateTime: item.startDateTime ?? item.startDate ?? '',
                    endDateTime: item.endDateTime ?? item.endDate ?? '',
                    teacherDisplayName: item.teacherDisplayName ?? null,
                    classroomName: item.classroomName ?? null,
                    status: item.status ?? 'Scheduled',
                    location: item.location ?? null,
                    meetingUrl: item.meetingUrl ?? null,
                }))
                    .filter((session) => Number.isFinite(new Date(session.startDateTime).getTime()))
                    .sort((first, second) => new Date(first.startDateTime).getTime() - new Date(second.startDateTime).getTime())
                    .slice(0, 3)
                : [];
            setUpcomingSessions(rows);
        } catch {
            // silently ignore — sessions widget is supplementary
        } finally {
            setSessionsLoading(false);
        }
    };

    const handleOpenAssignment = (assignment: AssignmentSummary) => {
        const organizationContext = getOrganizationContext();
        const isExamAssignment = assignment.type?.toLowerCase() === 'exam';

        if (isExamAssignment && assignment.examId) {
            const targetPath = organizationContext?.organizationSlug
                ? `/${organizationContext.organizationSlug}/exam-center/${assignment.examId}`
                : `/exam-center/${assignment.examId}`;

            navigate(targetPath);
            return;
        }

        const targetPath = organizationContext?.organizationSlug
            ? `/${organizationContext.organizationSlug}/assignments/${assignment.id}/homework`
            : `/assignments/${assignment.id}/homework`;

        navigate(targetPath);
    };

    const handleOpenHomeworkList = () => {
        const organizationContext = getOrganizationContext();
        const targetPath = organizationContext?.organizationSlug
            ? `/${organizationContext.organizationSlug}/assignments/homework`
            : '/assignments/homework';

        navigate(targetPath);
    };

    const handleOpenExamList = () => {
        const organizationContext = getOrganizationContext();
        const targetPath = organizationContext?.organizationSlug
            ? `/${organizationContext.organizationSlug}/practice-tests`
            : '/practice-tests';

        navigate(targetPath);
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently();
            const response = await fetch(`${config.apiBaseUrl}/api/analytics/dashboard`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...getOrganizationHeaders(),
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
            }

            const data = await response.json();
            setDashboard(data);
        } catch (err) {
            console.error('Error fetching dashboard:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="text-center py-12">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Analytics Dashboard</h1>
                <p className="text-gray-600 mb-4">Please log in to view your analytics</p>
                <button
                    onClick={() => loginWithRedirect()}
                    className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                >
                    Log In
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error || !dashboard) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Dashboard</h2>
                <p className="text-red-600">{error || 'No data available'}</p>
                <button
                    onClick={fetchDashboardData}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                    Retry
                </button>
            </div>
        );
    }

    const userProgress = dashboard.userProgress;
    const enrolledCourses = dashboard.enrolledCourses ?? [];
    const courseAnalytics = dashboard.courseAnalytics ?? [];
    const recentTestPerformance = dashboard.recentTestPerformance ?? [];
    const studyTrend = dashboard.studyTrend;
    const assignments = dashboard.assignments ?? [];
    const homeworkAssignments = assignments.filter((assignment) => assignment.type?.toLowerCase() !== 'exam');
    const examAssignments = assignments.filter((assignment) => assignment.type?.toLowerCase() === 'exam');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="mb-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Analytics Dashboard</h1>
                            <p className="text-gray-600">Track your learning progress and performance metrics</p>
                        </div>
                        <button
                            onClick={() => navigate('/analytics?pre=1')}
                            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                        >
                            Change Organization
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="mb-8 bg-white rounded-lg shadow-sm p-1">
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2">
                    {(['overview', 'courses', 'performance', 'statistics'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`w-full sm:w-auto px-3 sm:px-6 py-2 rounded-md font-medium text-sm sm:text-base transition ${
                                activeTab === tab
                                    ? 'bg-indigo-600 text-white'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                    </div>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* Key Metrics Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <MetricCard
                                title="Courses Completed"
                                value={userProgress.coursesCompleted}
                                total={userProgress.totalCoursesEnrolled}
                                color="bg-blue"
                            />
                            <MetricCard
                                title="Average Score"
                                value={userProgress.averagePracticeScore.toFixed(1)}
                                unit="%"
                                color="bg-green"
                            />
                            <MetricCard
                                title="Study Streak"
                                value={userProgress.currentStudyStreak}
                                unit="days"
                                color="bg-purple"
                            />
                            <MetricCard
                                title="Accuracy"
                                value={userProgress.overallAccuracy.toFixed(1)}
                                unit="%"
                                color="bg-orange"
                            />
                        </div>

                        {/* Upcoming Sessions */}
                        <div className="bg-white rounded-xl shadow-md border border-indigo-100 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-gray-800">Upcoming Sessions</h2>
                                <button
                                    type="button"
                                    onClick={() => navigate('/my-schedule')}
                                    className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                                >
                                    View all →
                                </button>
                            </div>
                            {sessionsLoading ? (
                                <p className="text-sm text-gray-400">Loading sessions...</p>
                            ) : upcomingSessions.length === 0 ? (
                                <p className="text-sm text-gray-500">No upcoming sessions scheduled.</p>
                            ) : (
                                <div className="space-y-3">
                                    {upcomingSessions.map((session) => {
                                        const start = new Date(session.startDateTime);
                                        const isValid = !Number.isNaN(start.getTime());
                                        const isInProgress = session.status === 'InProgress';
                                        return (
                                            <div key={session.id} className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h4 className="font-semibold text-gray-800">{session.title || `Session #${session.id}`}</h4>
                                                        {isInProgress && (
                                                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Live</span>
                                                        )}
                                                    </div>
                                                    <p className="mt-1 text-sm text-gray-500">
                                                        {isValid ? start.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {session.teacherDisplayName ? `Teacher: ${session.teacherDisplayName}` : ''}
                                                        {session.classroomName ? ` • ${session.classroomName}` : ''}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    {isInProgress && session.meetingUrl && (
                                                        <a
                                                            href={session.meetingUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                                                        >
                                                            Join
                                                        </a>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/my-session/${session.id}?source=analytics`)}
                                                        className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-gray-800">Assignments</h2>
                                <span className="text-sm text-gray-500">{assignments.length} item(s)</span>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-semibold text-gray-800">Homework</h3>
                                            <button
                                                type="button"
                                                onClick={handleOpenHomeworkList}
                                                className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                                            >
                                                View All Homeworks
                                            </button>
                                        </div>
                                        <span className="text-sm text-gray-500">{homeworkAssignments.length} item(s)</span>
                                    </div>
                                    {homeworkAssignments.length > 0 ? (
                                        <div className="space-y-3">
                                            {homeworkAssignments.map((assignment) => (
                                                <div key={assignment.id} className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-800">{assignment.title}</h4>
                                                        <p className="mt-1 text-sm text-gray-600">
                                                            {assignment.type || 'Assignment'}{assignment.examName ? ` • ${assignment.examName}` : ''}
                                                        </p>
                                                        {assignment.description && (
                                                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">{assignment.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-start gap-2 text-sm text-gray-600 sm:items-end">
                                                        <div className="font-medium text-indigo-700">{assignment.status || 'Published'}</div>
                                                        {assignment.dueAt && (
                                                            <div className="mt-1">Due: {new Date(assignment.dueAt).toLocaleDateString()}</div>
                                                        )}
                                                        <div className="mt-1">
                                                            {assignment.questionCount ?? 0} questions • {assignment.targetCount ?? 0} targets
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenAssignment(assignment)}
                                                            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                                                        >
                                                            Open Homework
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">No homework assignments available.</p>
                                    )}
                                </div>

                                <div>
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-semibold text-gray-800">Exams</h3>
                                            <button
                                                type="button"
                                                onClick={handleOpenExamList}
                                                className="inline-flex items-center rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                                            >
                                                View All Exams
                                            </button>
                                        </div>
                                        <span className="text-sm text-gray-500">{examAssignments.length} item(s)</span>
                                    </div>
                                    {examAssignments.length > 0 ? (
                                        <div className="space-y-3">
                                            {examAssignments.map((assignment) => (
                                                <div key={assignment.id} className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-800">{assignment.title}</h4>
                                                        <p className="mt-1 text-sm text-gray-600">
                                                            {assignment.type || 'Exam'}{assignment.examName ? ` • ${assignment.examName}` : ''}
                                                        </p>
                                                        {assignment.description && (
                                                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">{assignment.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-start gap-2 text-sm text-gray-600 sm:items-end">
                                                        <div className="font-medium text-indigo-700">{assignment.status || 'Published'}</div>
                                                        {assignment.dueAt && (
                                                            <div className="mt-1">Due: {new Date(assignment.dueAt).toLocaleDateString()}</div>
                                                        )}
                                                        <div className="mt-1">
                                                            {assignment.questionCount ?? 0} questions • {assignment.targetCount ?? 0} targets
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenAssignment(assignment)}
                                                            className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                                                        >
                                                            Open Exam
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500">No exams available.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                            {/* Enrolled Courses + Associated Exam Times (always left) */}
                            <div className="bg-white rounded-xl shadow-md border border-indigo-100 p-6 xl:col-span-8 order-1 xl:order-1">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-bold text-gray-800">Enrolled Courses</h2>
                                    <span className="text-sm text-gray-500">{enrolledCourses.length} course(s)</span>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">Tap a course to see full details and exam-time stats.</p>

                                {enrolledCourses.length > 0 ? (
                                    <div className="space-y-3">
                                        {enrolledCourses.map((course) => (
                                            <div key={course.courseId} className="border border-gray-200 rounded-xl overflow-hidden hover:bg-gray-50 transition">
                                                <div className="flex items-stretch">
                                                    <div className="w-20 bg-indigo-100 text-indigo-600 flex items-center justify-center" aria-hidden="true">
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H6.5A2.5 2.5 0 0 0 4 18.5v-13Z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 10h8" />
                                                        </svg>
                                                    </div>

                                                    <div className="flex-1 p-4">
                                                        <button
                                                            type="button"
                                                            onClick={() => setExpandedCourseId((prev) => (prev === course.courseId ? null : course.courseId))}
                                                            className="w-full text-left"
                                                        >
                                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                                <div>
                                                                    <h3 className="font-semibold text-base sm:text-lg text-gray-800">{course.courseName}</h3>
                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        Last activity: {course.lastPracticeAttemptDate
                                                                            ? new Date(course.lastPracticeAttemptDate).toLocaleDateString()
                                                                            : 'No attempts yet'}
                                                                    </p>
                                                                </div>
                                                                <span className={`text-xs font-semibold px-2 py-1 rounded-full w-fit ${course.completionPercentage >= 90
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : course.completionPercentage >= 50
                                                                        ? 'bg-amber-100 text-amber-700'
                                                                        : 'bg-blue-100 text-blue-700'}`}>
                                                                    {course.completionPercentage >= 90
                                                                        ? 'Completed'
                                                                        : course.completionPercentage >= 50
                                                                            ? 'In Progress'
                                                                            : 'Getting Started'}
                                                                </span>
                                                            </div>

                                                            <p className="mt-2 text-sm text-indigo-700 font-medium">
                                                                {course.completionPercentage.toFixed(1)}% complete
                                                            </p>
                                                        </button>

                                                        {expandedCourseId === course.courseId && (
                                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
                                                                    <div className="bg-blue-50 rounded p-2">
                                                                        <p className="text-gray-600 text-xs">Associated Exams</p>
                                                                        <p className="font-semibold text-blue-700">{course.associatedExamCount}</p>
                                                                    </div>
                                                                    <div className="bg-indigo-50 rounded p-2">
                                                                        <p className="text-gray-600 text-xs">Total Exam Time</p>
                                                                        <p className="font-semibold text-indigo-700">{course.totalExamMinutes} min</p>
                                                                    </div>
                                                                    <div className="bg-purple-50 rounded p-2">
                                                                        <p className="text-gray-600 text-xs">Avg Exam Time</p>
                                                                        <p className="font-semibold text-purple-700">{course.averageExamMinutes} min</p>
                                                                    </div>
                                                                    <div className="bg-green-50 rounded p-2">
                                                                        <p className="text-gray-600 text-xs">Enrolled On</p>
                                                                        <p className="font-semibold text-green-700">{new Date(course.enrollmentDate).toLocaleDateString()}</p>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-3 flex items-center justify-end">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setActiveTab('courses')}
                                                                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                                                    >
                                                                        View detailed analytics
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No enrolled courses found.</p>
                                )}
                            </div>

                            {/* Recent Performance (always right) */}
                            <div className="bg-white rounded-lg shadow-sm p-6 xl:col-span-4 order-2 xl:order-2">
                                <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Test Performance</h2>
                                {recentTestPerformance.length > 0 ? (
                                    <div className="space-y-3">
                                        {recentTestPerformance.slice(0, 4).map((test) => (
                                            <div key={test.testId} className="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                                                <div>
                                                    <h3 className="font-semibold text-sm sm:text-base text-gray-800">{test.testName}</h3>
                                                    <p className="text-xs sm:text-sm text-gray-500">{test.courseName}</p>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-base sm:text-lg font-bold text-indigo-600">{test.score.toFixed(1)}%</div>
                                                    <div className={`text-xs sm:text-sm font-medium ${test.isPassed ? 'text-green-600' : 'text-red-600'}`}>
                                                        {test.isPassed ? 'Passed' : 'Failed'}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {recentTestPerformance.length > 4 && (
                                            <button
                                                type="button"
                                                onClick={() => setActiveTab('performance')}
                                                className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                            >
                                                View all performance records
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No tests completed yet</p>
                                )}
                            </div>
                        </div>

                        {/* Study Trend */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Study Trend</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-gray-600">Average Daily Study</p>
                                    <p className="text-2xl font-bold text-blue-600">{studyTrend.averageDailyMinutes} min</p>
                                </div>
                                <div className="p-4 bg-green-50 rounded-lg">
                                    <p className="text-sm text-gray-600">Active Days</p>
                                    <p className="text-2xl font-bold text-green-600">{studyTrend.totalDaysActive}</p>
                                </div>
                                <div className="p-4 bg-purple-50 rounded-lg">
                                    <p className="text-sm text-gray-600">Weekly Trend</p>
                                    <p className={`text-2xl font-bold ${studyTrend.weeklyTrendPercentage >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                                        {studyTrend.weeklyTrendPercentage > 0 ? '+' : ''}{studyTrend.weeklyTrendPercentage.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Courses Tab */}
                {activeTab === 'courses' && (
                    <CourseAnalytics courses={courseAnalytics} />
                )}

                {/* Performance Tab */}
                {activeTab === 'performance' && (
                    <PerformanceChart recentTests={recentTestPerformance} studyTrend={studyTrend} />
                )}

                {/* Statistics Tab */}
                {activeTab === 'statistics' && (
                    <StudyStatistics userProgress={userProgress} />
                )}
        </div>
    );
}

interface MetricCardProps {
    title: string;
    value: number | string;
    unit?: string;
    total?: number;
    color: string;
}

function MetricCard({ title, value, unit, total, color }: MetricCardProps) {
    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-sm text-gray-600 font-medium mb-2">{title}</p>
            <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-bold text-${color}-600`}>{value}</span>
                {unit && <span className="text-gray-500">{unit}</span>}
                {total && <span className="text-gray-400 text-sm">/ {total}</span>}
            </div>
        </div>
    );
}
